import { Button, Input, message, Modal, Select } from "antd";
import React, { useEffect } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  UnitOfMeasurement,
  UnitOfMeasurementStatus,
} from "../../types/unit-of-measurement";
import {
  useCreateUnitOfMeasurement,
  useUpdateUnitOfMeasurement,
} from "../../api/unit-of-measurement";

const uomSchema = z.object({
  code: z
    .string()
    .min(1, { message: "Code is required" })
    .max(50, { message: "Code is too long" }),
  name: z
    .string()
    .min(1, { message: "Name is required" })
    .max(255, { message: "Name is too long" }),
  symbol: z
    .string()
    .min(1, { message: "Symbol is required" })
    .max(50, { message: "Symbol is too long" }),
  description: z
    .string()
    .max(500, { message: "Description is too long" })
    .optional()
    .or(z.literal("")),
  status: z.enum(["ACTIVE", "INACTIVE"]),
});

type UomForm = z.infer<typeof uomSchema>;

type UnitOfMeasurementModalProps = {
  open: boolean;
  selectedUom?: UnitOfMeasurement | null;
  hideModal: () => void;
};

const blankForm: UomForm = {
  code: "",
  name: "",
  symbol: "",
  description: "",
  status: "ACTIVE",
};

const statusOptions: { label: string; value: UnitOfMeasurementStatus }[] = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

const UnitOfMeasurementModal: React.FC<UnitOfMeasurementModalProps> = ({
  open,
  selectedUom,
  hideModal,
}) => {
  const { mutateAsync: createUom, isPending: creating } =
    useCreateUnitOfMeasurement();
  const { mutateAsync: updateUom, isPending: updating } =
    useUpdateUnitOfMeasurement();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<UomForm>({
    resolver: zodResolver(uomSchema) as any,
    defaultValues: blankForm,
    mode: "onChange",
  });

  useEffect(() => {
    if (!open) return;
    if (selectedUom) {
      reset({
        code: selectedUom.code ?? "",
        name: selectedUom.name ?? "",
        symbol: selectedUom.symbol ?? "",
        description: selectedUom.description ?? "",
        status: selectedUom.status ?? "ACTIVE",
      });
    } else {
      reset(blankForm);
    }
  }, [open, selectedUom, reset]);

  const onSubmit: SubmitHandler<UomForm> = async (form) => {
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      symbol: form.symbol.trim(),
      description: form.description?.trim() || null,
      status: form.status,
    };

    try {
      if (selectedUom?.id) {
        await updateUom({ id: selectedUom.id, payload });
        message.success("Unit of measurement updated successfully");
      } else {
        await createUom(payload);
        message.success("Unit of measurement created successfully");
      }
      hideModal();
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Operation failed";
      message.error(
        typeof apiMessage === "string" ? apiMessage : "Operation failed",
      );
    }
  };

  return (
    <Modal
      destroyOnClose
      open={open}
      centered
      onCancel={hideModal}
      title={
        <div className="flex flex-col items-center justify-center font-semibold text-black text-[22px]">
          {selectedUom ? "Edit Unit of Measurement" : "Create Unit of Measurement"}
        </div>
      }
      footer={
        <div className="flex justify-end gap-3 p-4">
          <Button
            onClick={hideModal}
            className="!border-gray-300 !text-gray-700 font-medium hover:!bg-gray-100 hover:!text-gray-900 transition-all duration-200 bg-transparent"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit(onSubmit)}
            className="!bg-[#009602] !border-none hover:!bg-[#00b306] font-medium transition-all duration-200"
            loading={creating || updating}
            disabled={creating || updating}
          >
            {selectedUom ? "Update" : "Create"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code *
            </label>
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="e.g. Bag"
                  status={errors.code ? "error" : undefined}
                />
              )}
            />
            {errors.code && (
              <span className="text-red-600 text-xs">{errors.code.message}</span>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Symbol *
            </label>
            <Controller
              name="symbol"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder="e.g. bag"
                  status={errors.symbol ? "error" : undefined}
                />
              )}
            />
            {errors.symbol && (
              <span className="text-red-600 text-xs">
                {errors.symbol.message}
              </span>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name *
          </label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="e.g. Bag"
                status={errors.name ? "error" : undefined}
              />
            )}
          />
          {errors.name && (
            <span className="text-red-600 text-xs">{errors.name.message}</span>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <Input.TextArea
                {...field}
                rows={3}
                placeholder="Optional description"
                status={errors.description ? "error" : undefined}
              />
            )}
          />
          {errors.description && (
            <span className="text-red-600 text-xs">
              {errors.description.message}
            </span>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status *
          </label>
          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                style={{ width: "100%" }}
                options={statusOptions}
              />
            )}
          />
        </div>
      </div>
    </Modal>
  );
};

export default UnitOfMeasurementModal;
